import { Pressable, StyleSheet, Text } from "react-native";
import type { Palette } from "@heraion/shared";
import { Icon } from "@heraion/ui";

// RW-52 — the web twin of mobile's CreateFab (apps/mobile/src/screens/
// CreateFab.tsx): a filled circle is the "raised" cue, not a shadow
// (DESIGN.md bans drop shadows on non-modal chrome). Shared by the wide rail
// (Sidebar) and the narrow bottom bar (TabBar) so the affordance means the
// same thing in both layouts — an ACTION, not a destination — and never
// reads as just another menu row.
//
// RW-52.1 (Antenore, after seeing v4.91.0-dev) — "il segno più tra workouts
// e Progress è brutto nel web, in Android è bello". On Android the FAB is a
// circle RAISED OVER a HORIZONTAL bar: an affordance sporting from an edge.
// The rail is a VERTICAL list, so the same circle there just reads as a ball
// wedged between two rows — the gesture was translated as a position
// ("centre of the nav") instead of as a shape-language ("stands apart from
// the destinations"). Fix: the rail gets its OWN rendering of the same
// gesture — a full-width, texted primary button ABOVE the list, separated by
// a rule — while the narrow bar keeps the exact raised-circle Android already
// gets right. Two `variant`s, one component: press/pressed-state/a11y wiring
// is identical in both shapes (only the box + whether a word renders differ),
// so one function keeps that logic in one place instead of forking it across
// two files that would drift.
export const CREATE_NAV_BUTTON_SIZE = 48;

export function CreateNavButton({
  p,
  onPress,
  accessibilityLabel,
  variant,
  label,
}: {
  p: Palette;
  onPress: () => void;
  accessibilityLabel: string;
  /** "fab" — the narrow bar's raised circle (Android's own shape, untouched).
   *  "full" — the wide rail's full-width labelled button (RW-52.1). */
  variant: "fab" | "full";
  /** Visible word next to the icon. Required for "full" — that variant's
   *  whole point is that Create stops being icon-only. Ignored for "fab". */
  label?: string;
}) {
  const styles = makeStyles(p);
  const full = variant === "full";
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      style={({ pressed }) => [
        full ? styles.full : styles.fab,
        pressed && (full ? styles.fullPressed : styles.fabPressed),
      ]}
    >
      {({ pressed }) => (
        <>
          <Icon
            name="plus"
            size={full ? 18 : 22}
            // DS-21 — a pressed brand fill darkens (brandPressed) and the ink
            // riding it follows the fill (inkOnBrandPressed), not the base
            // inkOnBrand: the two aren't guaranteed equal in every theme
            // family (rosepine-light is the documented exception). The old
            // icon-only fab predates that gate and keeps its single
            // inkOnBrand — out of this fix's scope, and it isn't gaining a
            // visible word, so it isn't the contrast risk the text below is.
            color={full && pressed ? p.inkOnBrandPressed : p.inkOnBrand}
          />
          {full && label ? (
            <Text
              style={[
                styles.fullLabel,
                pressed && { color: p.inkOnBrandPressed },
              ]}
            >
              {label}
            </Text>
          ) : null}
        </>
      )}
    </Pressable>
  );
}

function makeStyles(p: Palette) {
  return StyleSheet.create({
    fab: {
      width: CREATE_NAV_BUTTON_SIZE,
      height: CREATE_NAV_BUTTON_SIZE,
      borderRadius: CREATE_NAV_BUTTON_SIZE / 2,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: p.brand500,
      borderWidth: 3,
      borderColor: p.surface,
    },
    fabPressed: { backgroundColor: p.brand600 },
    // Full-width primary button (rail only). Rectangular + a plain radius
    // (matching the rail's own `item` rows), NEVER a full pill — the shape
    // gate bans it, and a pill here would read as a chip, not a CTA.
    full: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 44,
      borderRadius: 10,
      gap: 8,
      backgroundColor: p.brand500,
    },
    fullPressed: { backgroundColor: p.brandPressed },
    fullLabel: { color: p.inkOnBrand, fontSize: 15, fontWeight: "700" },
  });
}
